import {
  useMutation,
  UseMutationOptions,
  useQueryClient,
} from '@tanstack/react-query';

import { fetchList, FetchListQueryParams } from './fetchList';
import { Note, NoteLite } from '../types';
import { createOne } from './createOne';
import { fetchOne } from './fetchOne';
import { FilterKeys, NoteFilters } from '../components/Notes/NoteListView';
import { dateToString } from '../utils/date';

// Details
type UseNoteDetailsActionOptions = UseMutationOptions<
  NoteDetailsActionResponse,
  unknown,
  UseNoteDetailsActionParams
>;

type NoteDetailsActionResponse = Note;
type UseNoteDetailsActionParams = { id: string };

export const useNoteDetailsAction = (options?: UseNoteDetailsActionOptions) => {
  const queryClient = useQueryClient();

  return useMutation<
    NoteDetailsActionResponse,
    unknown,
    UseNoteDetailsActionParams
  >({
    mutationFn: ({ id }) => noteDetailsAction({ id }),
    ...options,
    onSuccess: (data, variables, onMutateResult, context) => {
      queryClient.invalidateQueries({ queryKey: ['notes'] });
      if (options?.onSuccess) {
        options.onSuccess(data, variables, onMutateResult, context);
      }
    },
  });
};

// List
type UseNoteListActionOptions = UseMutationOptions<
  NoteListActionResponse,
  unknown,
  UseNoteListActionParams
>;

type UseSubFilterNoteListActionOptions = UseMutationOptions<
  any,
  unknown,
  UseSubFiltersNoteLitesActionParams
>;

type NoteListActionResponse = {
  results: {
    hits: {
      hits: Array<NoteLite>;
      total: { value: number };
    };
  };
  count: number;
  pageSize?: number;
};

type NoteListActionParams = { query: string } & {
  [key in FilterKeys]: Array<string>;
};

type UseNoteListActionParams = NoteFilters & {
  query: string;
};

type UseSubFiltersNoteLitesActionParams = {
  filter: string;
  query: string;
};

export const useNoteLitesAction = (options?: UseNoteListActionOptions) => {
  const queryClient = useQueryClient();

  return useMutation<NoteListActionResponse, unknown, UseNoteListActionParams>({
    mutationFn: ({
      query,
      topic,
      requesters_unit_names,
      assigned_units_names,
      contributors,
      publication_date_before,
      publication_date_after,
      page,
    }) => {
      return notesLitesAction({
        query,
        topic: topic.map((filter) => filter.value as string),
        requesters_unit_names: requesters_unit_names.map(
          (filter) => filter.value as string,
        ),
        assigned_units_names: assigned_units_names.map(
          (filter) => filter.value as string,
        ),
        publication_date_after: publication_date_after.map(
          (filter) => dateToString(filter.value as Date) as string,
        ),
        publication_date_before: publication_date_before.map(
          (filter) => dateToString(filter.value as Date) as string,
        ),
        contributors: contributors.map((filter) => filter.value as string),
        page: page.map((filter) => filter.value as string),
      });
    },
    ...options,
    onSuccess: (data, variables, onMutateResult, context) => {
      queryClient.invalidateQueries({ queryKey: ['notes'] });
      if (options?.onSuccess) {
        options.onSuccess(data, variables, onMutateResult, context);
      }
    },
  });
};

export const noteDetailsAction = (params: UseNoteDetailsActionParams) => {
  return fetchOne({ queryKey: ['notes', params.id], meta: undefined } as any);
};

export const notesLitesAction = (params: NoteListActionParams) => {
  return fetchList({
    queryKey: ['noteslites', params as FetchListQueryParams],
    meta: undefined,
  } as any);
};

export const useFiltersNoteLitesAction = (
  options?: UseSubFilterNoteListActionOptions,
) => {
  return useMutation<any, unknown, any>({
    mutationFn: () => filtersNotesLitesAction(),
    onSuccess: (data, variables, onMutateResult, context) => {
      if (options?.onSuccess) {
        options.onSuccess(data, variables, onMutateResult, context);
      }
    },
  });
};

export const filtersNotesLitesAction = () => {
  return createOne({
    name: 'noteslites/filters',
    payload: {},
  });
};
